import React, { useEffect, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import { FiDownload, FiMail, FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { API_ORIGIN } from '../../../../config/api';
import { graphqlRequest } from '../../../../utils/graphqlRequest';
import { downloadElementPdf } from '../../../../utils/downloadElementPdf';
import { uploadBookingFile } from '../../../../utils/uploadBookingFile';
import { formatDate } from '../customerUtils';
import { cleanTaskDescription } from '../../../../utils/taskDisplay';
import { collectResourcePhotos, collectVisitPhotos, parseNotes } from './visitPhotos';
import { allocationMap, visitResources } from './ProjectResourcesPanel';

/** Project tasks with assignees, as the manager portal's project view loads them. */
export const PROJECT_TASKS = `
  query WorkReportTasks($projectId: String!) {
    findTasksByProject(projectId: $projectId) {
      id
      title
      description
      status
      assignees {
        id
      }
    }
  }
`;

/** Quantities the office allocated to the project (older reports lack them). */
const PROJECT_RESOURCES = `
  query WorkReportResources($projectId: String) {
    getResourcesByProject(projectId: $projectId) {
      id
      quantityAssigned
      updateAction
      tool {
        name
      }
    }
  }
`;

const SEND_CUSTOM_MAIL = `
  mutation ProjectReportMail(
    $subject: String!
    $info: String!
    $bookingId: String!
    $paymentLink: Boolean!
    $to: String!
    $fileKey: String
  ) {
    sendCustomMail(
      subject: $subject
      info: $info
      bookingId: $bookingId
      paymentLink: $paymentLink
      to: $to
      fileKey: $fileKey
    )
  }
`;

function clockMinutes(value) {
  const match = String(value || '').match(/(\d{1,2}):(\d{2})/);
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
}

/** Worked minutes for a report, from `duration` text or the clock times. */
export function minutesOf(report) {
  const raw = String(report?.duration || '');
  const hours = raw.match(/(\d+)\s*h(?:our)?/i);
  const mins = raw.match(/(\d+)\s*m(?:in)?/i);
  if (hours || mins) {
    return (hours ? Number(hours[1]) * 60 : 0) + (mins ? Number(mins[1]) : 0);
  }
  const start = clockMinutes(report?.timeIn);
  const end = clockMinutes(report?.timeOut);
  if (start == null || end == null) return 0;
  return end >= start ? end - start : end + 24 * 60 - start;
}

export function formatMinutes(total) {
  if (!total) return '—';
  const h = Math.floor(total / 60);
  const m = total % 60;
  return [h ? `${h}h` : null, m ? `${m}m` : null].filter(Boolean).join(' ') || '0m';
}

/**
 * Tasks rostered for the visit with whether the cleaner ticked each one.
 * `completedTaskDetails` only lists ticked tasks, so the assigned set comes
 * from `visitTasks` when the report carries it.
 */
const list = (value) => (Array.isArray(value) ? value : []);

export function assignedTasks(notes, projectTasks = []) {
  const manual = notes.manualReport || {};
  const details = list(manual.completedTaskDetails).length
    ? list(manual.completedTaskDetails)
    : list(notes.completedTaskDetails);
  const doneIds = new Set([
    ...list(manual.completedTasks),
    ...list(notes.completedTasks),
    ...details.filter((t) => t?.completed !== false).map((t) => t?.id),
  ].filter(Boolean).map(String));
  // Rostered tasks first, then tasks the cleaner marked on the report.
  // Checklist lines are a separate list; they are not extra tasks.
  const roster = list(notes.rosterTasks);
  const visit = list(notes.visitTasks);
  const base = roster.length ? roster : visit.length ? visit : projectTasks;
  const seen = new Set();
  const assigned = [...base, ...details].filter((task) => {
    const title = task?.title || task?.name || task?.label;
    const key = String(task?.id || task?.taskId || title || '');
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  // The manager closing the visit (or approving the report) completes its tasks,
  // even when the cleaner didn't tick each one.
  const closedByManager =
    String(notes.reportStatus || '').toUpperCase() === 'APPROVED' ||
    String(notes.assignmentCompletion || '').toUpperCase() === 'COMPLETED' ||
    String(notes.visitStatus || '').toUpperCase() === 'COMPLETED' ||
    /^complete/i.test(String(notes.projectRecordStatus || ''));
  // Task status 'approve' is the stored value for a completed task.
  const taskClosed = (t) => /^(approve|complete)/i.test(String(t?.status || ''));
  return assigned.map((t, i) => {
    const title = t?.title || t?.name || t?.label || `Task ${i + 1}`;
    const raw = details.find((d) => d?.id && d.id === t?.id)?.description || t?.description || '';
    const { description, roleLabel } = cleanTaskDescription(raw, title);
    return {
      id: t?.id || i,
      title,
      description: [description, roleLabel ? `Role: ${roleLabel}` : ''].filter(Boolean).join('\n'),
      done:
        closedByManager ||
        taskClosed(t) ||
        (t?.id ? doneIds.has(String(t.id)) : t?.completed !== false),
    };
  });
}

/** Project tasks this cleaner is assigned to (all of them if none name the cleaner). */
export function tasksFor(userId, projectTasks = []) {
  const mine = projectTasks.filter((t) =>
    list(t.assignees).some((a) => String(a?.id) === String(userId)),
  );
  return mine.length ? mine : projectTasks;
}

export function taskProgress(notes, projectTasks = []) {
  const tasks = assignedTasks(notes, projectTasks);
  if (!tasks.length) return null;
  return { done: tasks.filter((t) => t.done).length, total: tasks.length };
}

export const isApproved = (report) =>
  String(report?.approvalStatus || '').toUpperCase() === 'APPROVED';

/** Combined figures for a set of cleaner reports (the "service report"). */
export function summarise(reports) {
  const cleaners = new Set(reports.map((r) => r.userId || r.employeeName));
  const minutes = reports.reduce((sum, r) => sum + minutesOf(r), 0);
  const photos = reports.reduce((sum, r) => sum + collectVisitPhotos(r).length, 0);
  const approved = reports.length > 0 && reports.every(isApproved);
  const latest = [...reports].sort((a, b) => (a.date < b.date ? 1 : -1))[0];
  const approvers = [...new Set(reports.map((r) => r.approvedByName).filter(Boolean))];
  return { cleaners: cleaners.size, minutes, photos, approved, latest, approvers };
}

const pretty = (value) =>
  String(value || '')
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());

function reportPhotoLinks(reportList) {
  const links = [];
  reportList.forEach((report) => {
    [...collectVisitPhotos(report), ...collectResourcePhotos(report)].forEach((photo) => {
      const url = String(photo.src || '');
      if (!/^https?:\/\//i.test(url)) return;
      const kind = photo.kind || 'Resource';
      links.push({
        label: photo.label ? `${kind} photo - ${photo.label}` : `${kind} photo`,
        url,
      });
    });
  });
  return links;
}

const emailText = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

function workReportEmailHtml(reportList) {
  const line = (label, value) =>
    value
      ? `<p style="margin:0 0 6px;"><strong>${label}:</strong> ${emailText(value)}</p>`
      : '';
  const unique = (values) => [...new Set(values.filter(Boolean))];
  const projects = unique(
    reportList.map((report) =>
      [report.projectCode, report.projectName].filter(Boolean).join(' · '),
    ),
  );
  const people = unique(reportList.map((report) => report.employeeName));
  const dates = unique(reportList.map((report) => formatDate(report.date)));
  const sites = unique(
    reportList.map((report) => parseNotes(report.notesJson).siteLocation),
  );
  const minutes = reportList.reduce((sum, report) => sum + minutesOf(report), 0);
  const photos = reportPhotoLinks(reportList).length;
  const projectLabel = projects.join(', ') || 'this project';

  return `<p style="margin:0 0 12px;">Please find the work report for <strong>${emailText(projectLabel)}</strong>.</p>
    ${line('Date', dates.join(', '))}
    ${line('Team', people.join(', '))}
    ${line('Site', sites.join(', '))}
    ${line('Time on site', formatMinutes(minutes))}
    <p style="margin:12px 0 0;">The full report is attached as a PDF. Use the button below to download it.${
      photos ? ' The last page of the PDF has a link for each photo.' : ''
    }</p>`;
}

const STATUS_TONE = {
  APPROVED: 'tone-ok',
  SUBMITTED: 'tone-info',
  RESUBMITTED: 'tone-info',
  REVISION_REQUESTED: 'tone-warn',
  INSPECTION_REQUIRED: 'tone-warn',
  REJECTED: 'tone-bad',
};

function formatStamp(value) {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
}

function Section({ title, count, children }) {
  return (
    <section className="pwr-section">
      <h4>
        {title}
        {count != null ? <em>{count}</em> : null}
      </h4>
      {children}
    </section>
  );
}

function Info({ label, value }) {
  if (value == null || value === '') return null;
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function PhotoGrid({ photos }) {
  const [shown, setShown] = useState(photos);
  const photoKey = photos.map((photo) => photo.key || photo.src).join('|');

  useEffect(() => {
    let live = true;
    const created = [];
    const token = Cookies.get('accessToken');
    Promise.all(
      photos.map(async (photo) => {
        const key = String(photo.key || '');
        if (!key.startsWith('work-reports/') || !token) return photo;
        const res = await fetch(
          `${API_ORIGIN}/work-reports/photo?key=${encodeURIComponent(key)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok) return photo;
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        created.push(url);
        return { ...photo, src: url, downloadUrl: photo.downloadUrl || photo.src };
      }),
    )
      .then((next) => {
        if (live) setShown(next);
      })
      .catch(() => {
        if (live) setShown(photos);
      });
    return () => {
      live = false;
      created.forEach((url) => URL.revokeObjectURL(url));
    };
    // photoKey captures the attachment set for this grid.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoKey]);

  if (!shown.length) return <p className="pwr-muted">None</p>;
  return (
    <div className="pfc-report-photos">
      {shown.map((p) => (
        <figure key={p.id}>
          <a href={p.downloadUrl || p.src} target="_blank" rel="noopener noreferrer">
            <img src={p.src} alt={p.label} />
          </a>
          {p.kind ? (
            <figcaption className={p.kind === 'After' ? 'is-after' : 'is-before'}>{p.kind}</figcaption>
          ) : null}
        </figure>
      ))}
    </div>
  );
}

const REVIEW_WORK_REPORT = `
  mutation AdminReviewWorkReport($input: ReviewWorkReportInput!) {
    reviewWorkReport(input: $input) {
      id
      status
    }
  }
`;

const REVIEWABLE = ['SUBMITTED', 'RESUBMITTED', 'INSPECTION_REQUIRED'];

/** Admin decision on a report the manager can't review (their own / no manager). */
function AdminReview({ reportId, onReviewed }) {
  const [feedback, setFeedback] = useState('');
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);

  const decide = async (status) => {
    if (status === 'REVISION_REQUESTED' && !feedback.trim()) {
      toast.error('Say what needs to change before requesting a revision.');
      return;
    }
    setBusy(true);
    try {
      await graphqlRequest(REVIEW_WORK_REPORT, {
        input: { reportId, status, feedback: feedback.trim() || null },
      });
      toast.success(status === 'APPROVED' ? 'Work report approved' : 'Revision requested');
      onReviewed?.();
    } catch (err) {
      toast.error(err?.message || 'Could not save the review');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pwr-admin-review" data-html2canvas-ignore>
      {asking ? (
        <textarea
          rows={3}
          value={feedback}
          maxLength={2000}
          placeholder="What needs to change?"
          onChange={(e) => setFeedback(e.target.value)}
        />
      ) : null}
      <span>
        {asking ? (
          <>
            <button type="button" className="pdx-btn" disabled={busy} onClick={() => setAsking(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="pdx-btn"
              disabled={busy}
              onClick={() => decide('REVISION_REQUESTED')}
            >
              {busy ? 'Sending…' : 'Send revision request'}
            </button>
          </>
        ) : (
          <>
            <button type="button" className="pdx-btn" disabled={busy} onClick={() => setAsking(true)}>
              Request changes
            </button>
            <button
              type="button"
              className="pdx-btn pdx-btn--primary"
              disabled={busy}
              onClick={() => decide('APPROVED')}
            >
              {busy ? 'Approving…' : 'Approve'}
            </button>
          </>
        )}
      </span>
    </div>
  );
}

function ReportBlock({ report, allocations, projectTasks = [], onReviewed }) {
  const notes = parseNotes(report.notesJson);
  const manual = notes.manualReport || {};
  const summary = notes.reportSummary || manual.summary || '';
  const statusKey = String(
    notes.reportStatus || (isApproved(report) ? 'APPROVED' : 'SUBMITTED'),
  ).toUpperCase();
  const tasks = assignedTasks(notes, tasksFor(report.userId, projectTasks));
  const checklist = list(notes.assignmentChecklist?.checklist).length
    ? notes.assignmentChecklist.checklist
    : list(notes.checklistProgress?.checklist).length
      ? notes.checklistProgress.checklist
      : list(manual.checklistProgress?.checklist);
  const incompleteCode =
    notes.incompleteReasonCode || manual.incompleteReasonCode || notes.assignmentIncompleteCode;
  const incompleteNote =
    notes.incompleteReasonNote || manual.incompleteReasonNote || notes.assignmentIncompleteNote;
  const resources = visitResources(report.notesJson, allocations);
  const photos = collectVisitPhotos(report);
  const before = photos.filter((p) => p.kind === 'Before');
  const after = photos.filter((p) => p.kind === 'After');
  const resourcePhotos = collectResourcePhotos(report);
  const approver = notes.projectManagerName;
  // No self-approval: the manager's own report (or a manager-less project) is the admin's.
  const adminReviews =
    !notes.projectManagerId || String(notes.projectManagerId) === String(report.userId);

  return (
    <article className="pfc-report pwr">
      <header className="pwr-head">
        <div>
          <strong>{report.employeeName || 'Cleaner'}</strong>
          <span>
            {[report.projectCode, report.projectName].filter(Boolean).join(' · ') || 'Work report'}
          </span>
        </div>
        <span className="pwr-badges">
          {notes.isManual || notes.reportKind === 'manual' ? (
            <span className="pdx-status tone-mute">Manual entry</span>
          ) : null}
          <span className={`pdx-status ${STATUS_TONE[statusKey] || 'tone-mute'}`}>
            {pretty(statusKey)}
          </span>
        </span>
      </header>

      <dl className="pwr-grid">
        <Info label="Visit date" value={formatDate(report.date)} />
        <Info label="Clock in" value={report.timeIn || '—'} />
        <Info label="Clock out" value={report.timeOut || '—'} />
        <Info label="Time on site" value={formatMinutes(minutesOf(report))} />
        <Info label="Site" value={notes.siteLocation} />
        <Info label="Work status" value={pretty(notes.workStatus || manual.workStatus)} />
        <Info label="Project status" value={pretty(notes.projectStatus || manual.projectStatus)} />
        <Info label="Submitted" value={formatStamp(notes.submittedAt)} />
        <Info
          label="Revision"
          value={notes.revisionNumber > 1 ? `#${notes.revisionNumber}` : ''}
        />
      </dl>

      {notes.manualReason ? (
        <p className="pwr-note">
          <strong>Reason for no clock-in:</strong> {String(notes.manualReason)}
        </p>
      ) : null}

      <Section
        title="Assigned tasks"
        count={tasks.length ? `${tasks.filter((t) => t.done).length}/${tasks.length} done` : null}
      >
        {tasks.length ? (
          <ul className="pwr-checks">
            {tasks.map((t) => (
              <li key={t.id} className={t.done ? 'is-done' : 'is-open'}>
                <span aria-hidden>{t.done ? '✓' : '✗'}</span>
                <div>
                  {t.title}
                  {t.description ? <small>{t.description}</small> : null}
                </div>
                <em>{t.done ? 'Completed' : 'Not completed'}</em>
              </li>
            ))}
          </ul>
        ) : (
          <p className="pwr-muted">No tasks recorded on this report.</p>
        )}
      </Section>

      {checklist.length ? (
        <Section
          title="Checklist"
          count={`${checklist.filter((c) => c?.completed === true).length}/${checklist.length}`}
        >
          <ul className="pwr-checks">
            {checklist.map((c, i) => {
              const step = c?.label || c?.title || c?.task || '';
              const key = String(c?.key || '');
              const taskId = key.includes(':') ? key.slice(0, key.indexOf(':')) : key;
              const parent = [...list(notes.rosterTasks), ...list(notes.visitTasks)]
                .find((t) => String(t?.id) === taskId)?.title;
              const name =
                parent && step && parent !== step && !String(step).startsWith(`${parent} — `)
                  ? `${parent} — ${step}`
                  : step || parent || `Item ${i + 1}`;
              return (
              <li key={c?.id || c?.key || i} className={c?.completed === true ? 'is-done' : 'is-open'}>
                <span aria-hidden>{c?.completed === true ? '✓' : '✗'}</span>
                <div>{name}</div>
                <em>{c?.required === false ? 'Optional' : 'Required'}</em>
              </li>
              );
            })}
          </ul>
        </Section>
      ) : null}

      {incompleteCode ? (
        <p className="pwr-note pwr-note--warn">
          <strong>Incomplete work — {pretty(incompleteCode)}:</strong>{' '}
          {incompleteNote || 'No explanation given.'}
        </p>
      ) : null}

      <Section title="Resources allocated & used" count={resources.length || null}>
        {resources.length ? (
          <div className="pdx-table-wrap">
            <table className="pdx-table pwr-table">
              <thead>
                <tr>
                  <th>Resource</th>
                  <th>Type</th>
                  <th>Allocated</th>
                  <th>Used</th>
                  <th>Condition</th>
                </tr>
              </thead>
              <tbody>
                {resources.map((r, i) => {
                  const allocated = r?.assigned;
                  return (
                    <tr key={r?.id || i}>
                      <td>
                        <strong>{r?.name || 'Resource'}</strong>
                      </td>
                      <td>{r?.type || '—'}</td>
                      <td>{allocated != null && allocated !== '' ? allocated : '—'}</td>
                      <td>{r?.qty ? `${r.qty}${/[a-z]/i.test(r.qty) ? '' : ` ${r.unit || ''}`}` : '—'}</td>
                      <td>
                        {r?.status ? (
                          <span
                            className={`pdx-status ${
                              r.status === 'good'
                                ? 'tone-ok'
                                : r.status === 'low'
                                  ? 'tone-warn'
                                  : 'tone-bad'
                            }`}
                          >
                            {pretty(r.status)}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="pwr-muted">No resource usage recorded.</p>
        )}
        {resourcePhotos.length ? <PhotoGrid photos={resourcePhotos} /> : null}
      </Section>

      <Section title="Work summary">
        <p className="pfc-report-text">{summary ? String(summary) : 'No summary written.'}</p>
      </Section>

      <div className="pwr-photo-cols">
        <Section title="Before photos" count={before.length}>
          <PhotoGrid photos={before} />
        </Section>
        <Section title="After photos" count={after.length}>
          <PhotoGrid photos={after} />
        </Section>
      </div>

      <footer className="pwr-review">
        {statusKey === 'APPROVED' ? (
          <span>
            <strong>Approved</strong>
            {report.approvedByName ? ` by ${report.approvedByName}` : ''}
            {notes.reviewedAt ? ` · ${formatStamp(notes.reviewedAt)}` : ''}
          </span>
        ) : (
          <span>
            <strong>Awaiting review</strong> —{' '}
            {!adminReviews
              ? `to be approved by ${approver} (assigned project manager) in the manager portal → Work Report.`
              : notes.projectManagerId
                ? `this is the project manager's own report, so a company admin approves it here.`
                : 'no project manager is assigned, so a company admin approves it here.'}
          </span>
        )}
        {adminReviews && REVIEWABLE.includes(statusKey) && report.workReportId ? (
          <AdminReview reportId={report.workReportId} onReviewed={onReviewed} />
        ) : null}
        {notes.managerFeedback ? (
          <p className="pfc-report-text pfc-report-feedback">
            <strong>Manager feedback:</strong> {String(notes.managerFeedback)}
          </p>
        ) : null}
      </footer>
    </article>
  );
}

/**
 * One cleaner's report, or (with several reports) the combined service
 * report for the selected period.
 */
const mailStarted = new Set();

export default function ProjectWorkReportModal({
  reports,
  title,
  bookingId,
  projectId: projectIdProp,
  projectTasks = [],
  clientEmail,
  fileStem,
  onClose,
  onReviewed,
  autoEmail = false,
  onAutoEmailDone,
}) {
  const bodyRef = useRef(null);
  const [busy, setBusy] = useState('');
  const [allocations, setAllocations] = useState(() => new Map());
  const combined = reports.length > 1;
  const stats = summarise(reports);
  const projectId = projectIdProp || parseNotes(reports[0]?.notesJson).projectId;

  // Same source as the manager portal's project view.
  useEffect(() => {
    if (!projectId) return undefined;
    let live = true;
    graphqlRequest(PROJECT_RESOURCES, { projectId })
      .then((res) => {
        if (!live) return;
        setAllocations(allocationMap(res?.getResourcesByProject));
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [projectId]);

  const downloadPdf = async () => {
    if (!bodyRef.current) return;
    setBusy('pdf');
    try {
      await downloadElementPdf(bodyRef.current, `${fileStem || 'work-report'}.pdf`, {
        links: reportPhotoLinks(reports),
      });
    } catch (err) {
      console.error('Work report export failed:', err);
      toast.error('Could not generate the report PDF.');
    } finally {
      setBusy('');
    }
  };

  const emailClient = async () => {
    const to = String(clientEmail || '').trim();
    if (!to) {
      toast.error('This client has no email address on file.');
      return;
    }
    if (!bodyRef.current) return;
    setBusy('mail');
    try {
      const filename = `${fileStem || 'work-report'}.pdf`;
      const blob = await downloadElementPdf(bodyRef.current, filename, {
        output: 'blob',
        links: reportPhotoLinks(reports),
      });
      const file = new File([blob], filename, { type: 'application/pdf' });
      const uploaded = await uploadBookingFile(file, filename);
      await graphqlRequest(SEND_CUSTOM_MAIL, {
        subject: title,
        info: workReportEmailHtml(reports),
        bookingId,
        paymentLink: false,
        fileKey: uploaded.key,
        to,
      });
      toast.success(`Report PDF emailed to ${to}`);
    } catch (err) {
      toast.error(err?.message || 'Failed to email the report');
    } finally {
      setBusy('');
    }
  };

  useEffect(() => {
    if (!autoEmail) return undefined;
    const id = reports[0]?.id || 'report';
    let live = true;
    let timer = 0;
    const started = Date.now();
    const tick = () => {
      if (!live) return;
      const imgs = bodyRef.current ? [...bodyRef.current.querySelectorAll('img')] : [];
      const pending = imgs.some((img) => !img.complete || !img.naturalWidth);
      if (pending && Date.now() - started < 4000) {
        timer = window.setTimeout(tick, 200);
        return;
      }
      if (mailStarted.has(id)) return;
      mailStarted.add(id);
      emailClient().finally(() => {
        mailStarted.delete(id);
        onAutoEmailDone?.();
      });
    };
    timer = window.setTimeout(tick, 400);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
    // Fires once when Send opens this report.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoEmail, reports[0]?.id]);

  return (
    <div
      className="pdx-modal-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="pdx-modal pwr-modal" role="dialog" aria-modal="true" aria-label={title}>
        <div className="pdx-modal-head">
          <h3>{title}</h3>
          <span className="pfc-lightbox-actions">
            <button
              type="button"
              className="pdx-btn"
              onClick={emailClient}
              disabled={Boolean(busy) || !stats.approved}
              title={stats.approved ? '' : 'Only approved reports can be sent to the client'}
            >
              <FiMail size={14} />
              {busy === 'mail' ? 'Sending…' : 'Email client'}
            </button>
            <button
              type="button"
              className="pdx-btn pdx-btn--primary"
              onClick={downloadPdf}
              disabled={Boolean(busy)}
            >
              <FiDownload size={14} />
              {busy === 'pdf' ? 'Preparing…' : 'Download PDF'}
            </button>
            <button type="button" className="pdx-modal-close" aria-label="Close" onClick={onClose}>
              <FiX />
            </button>
          </span>
        </div>
        <div className="pdx-modal-body" ref={bodyRef}>
          {combined ? (
            <div className="pfc-report-summary">
              <div>
                <strong>{stats.cleaners}</strong>
                <span>Cleaner{stats.cleaners === 1 ? '' : 's'}</span>
              </div>
              <div>
                <strong>{reports.length}</strong>
                <span>Visit reports</span>
              </div>
              <div>
                <strong>{formatMinutes(stats.minutes)}</strong>
                <span>Combined time</span>
              </div>
              <div>
                <strong>{stats.photos}</strong>
                <span>Photos</span>
              </div>
            </div>
          ) : null}
          {reports.map((report) => (
            <ReportBlock
              key={report.id}
              report={report}
              allocations={allocations}
              projectTasks={projectTasks}
              onReviewed={onReviewed}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
